import { Trans, useLingui } from '@lingui/react/macro';
import { useEffect, useState } from 'react';

import { Button } from '@/components/ui/button';
import { BaseModal } from '@/components/ui/base-modal';
import { Input } from '@/components/ui/input';
import type { SavedTemplate } from '@/types/saved-template';

/**
 * Rename-only duplicate modal (TM2). The salvaged `duplicateTemplate`
 * API accepts `{ id, newName? }`; scope-changing on duplicate was
 * dropped from the evo flow, so this modal asks only for a new name.
 *
 * Default name seeded as "<original> (Copy)" matching the old app's
 * convention. Confirm is disabled when the trimmed name is empty;
 * Cancel + dialog dismissal both fire `onCancel`.
 */
export function DuplicateTemplateModal({
  template,
  onConfirm,
  onCancel,
}: DuplicateTemplateModalProps) {
  const { t } = useLingui();
  const [name, setName] = useState(`${template.name} (Copy)`);

  // Reseed only when the parent rotates to a different template
  // (different `id`). Listening on `template.name` would clobber the
  // user's in-progress typing whenever the parent re-renders with an
  // optimistically-updated name.
  useEffect(() => {
    setName(`${template.name} (Copy)`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [template.id]);

  /**
   * Submits the rename. No-op when the trimmed name is empty (the
   * confirm button is also disabled in that state — belt + braces).
   */
  const handleConfirm = () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    onConfirm({ id: template.id, newName: trimmed });
  };

  return (
    <BaseModal
      open
      onOpenChange={(open) => !open && onCancel()}
      title={<Trans>Duplicate template</Trans>}
      description={<Trans>Create a copy of &ldquo;{template.name}&rdquo;</Trans>}
      className="sm:max-w-[500px]"
      footer={
        <>
          <Button variant="outline" onClick={onCancel}>
            <Trans>Cancel</Trans>
          </Button>
          <Button onClick={handleConfirm} disabled={!name.trim()}>
            <Trans>Create duplicate</Trans>
          </Button>
        </>
      }
    >
      <div className="space-y-2 py-4">
        <label htmlFor="duplicate-template-name" className="text-sm font-medium">
          <Trans>New template name</Trans> <span className="text-destructive">*</span>
        </label>
        <Input
          id="duplicate-template-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={t`Enter template name`}
          autoFocus
        />
      </div>
    </BaseModal>
  );
}

type DuplicableTemplate = Pick<SavedTemplate, 'id' | 'name'>;

interface DuplicateTemplateModalProps {
  template: DuplicableTemplate;
  onConfirm: (args: { id: string; newName: string }) => void;
  onCancel: () => void;
}
